/**
 * Pure transform: converts a raw workflowPasses Convex document into a
 * RefinementPassRow view-model suitable for direct use by screen components.
 *
 * Computes belowConvergenceThreshold using strict less-than comparison (FR-005).
 * Emits a structured WARN log (transform.unknownStatus) on unrecognized status.
 *
 * Never throws on any valid Convex document shape.
 */
import type { Doc } from "../../../../../convex/_generated/dataModel";
import type { RefinementPassRow } from "../types/refinementRun";

// ---------------------------------------------------------------------------
// PASS_STATUS_CONFIG — pass-level status display labels
//
// Maps raw backend pass status strings to user-facing labels.
// The `_unknown` entry provides the graceful fallback for unrecognized values.
// ---------------------------------------------------------------------------

const PASS_STATUS_CONFIG: Record<string, { label: string }> = {
  running: { label: "In progress" },
  completed: { label: "Succeeded" },
  failed: { label: "Failed" },
  skipped: { label: "Skipped" },
  _unknown: { label: "Unknown" },
};

// ---------------------------------------------------------------------------
// Transform
// ---------------------------------------------------------------------------

/**
 * Converts a raw `workflowPasses` Convex document into a `RefinementPassRow`
 * view-model. Pure function — no side effects, no network calls, never throws.
 *
 * @param doc              The raw Convex document
 * @param contentThreshold The run-level content convergence threshold
 * @param structuralThreshold The run-level structural convergence threshold
 * @param contentDelta     Content delta from convergence record (undefined if unavailable)
 * @param structuralDelta  Structural delta from convergence record (undefined if unavailable)
 */
export function transformWorkflowPass(
  doc: Doc<"workflowPasses">,
  contentThreshold: number,
  structuralThreshold: number,
  contentDelta: number | undefined,
  structuralDelta: number | undefined,
): RefinementPassRow {
  // Status mapping (optional — passes may not have a status field)
  const rawStatus = (doc as Record<string, unknown>)["status"] as
    | string
    | undefined;
  let statusKey: string | undefined;
  let statusLabel: string | undefined;

  if (rawStatus != null) {
    statusKey = rawStatus;
    const entry = PASS_STATUS_CONFIG[rawStatus];
    if (entry) {
      statusLabel = entry.label;
    } else {
      statusLabel = PASS_STATUS_CONFIG["_unknown"]!.label;
      // Emit structured WARN log for observability (NFR-009)
      console.warn(
        JSON.stringify({
          event: "transform.unknownStatus",
          status: rawStatus,
          entity: "workflowPass",
          fallback: "_unknown",
        }),
      );
    }
  }

  // Convergence threshold comparison: strict less-than (FR-005)
  let belowConvergenceThreshold: boolean | undefined;
  if (contentDelta != null && structuralDelta != null) {
    belowConvergenceThreshold =
      contentDelta < contentThreshold && structuralDelta < structuralThreshold;
  }

  return {
    id: doc._id,
    passNumber: doc.passNumber,

    // Status (optional)
    statusKey,
    statusLabel,

    // Convergence
    belowConvergenceThreshold,
    contentDelta,
    structuralDelta,

    // Cost
    costUsd: doc.costUsd,
    tokenUsage: doc.tokenUsage,

    // Artifact
    artifactUri: (doc as Record<string, unknown>)["artifactUri"] as
      | string
      | undefined,

    // Timestamp
    createdAtMs: doc.createdAt ?? doc._creationTime,

    // Detections
    detections: doc.detections,
  };
}
